<template>
  <div>
    <b-card
      no-body
      class="mb-2 performance-toolbar-card"
    >
      <b-card-body class="performance-toolbar">
        <div class="toolbar-field market-field">
          <label>Market</label>
          <b-form-select
            v-model="market"
            class="performance-select"
            :options="marketOptions"
            @change="onMarketChange"
          />
        </div>

        <div class="toolbar-field auto-field">
          <label>İndikatörü otomatik belirle</label>
          <div class="auto-control">
            <b-form-checkbox
              :checked="settings.enabled"
              switch
              size="lg"
              class="performance-switch"
              :disabled="saving"
              @change="onToggleAuto"
            >
              {{ settings.enabled ? 'Aktif' : 'Kapalı' }}
            </b-form-checkbox>
            <b-badge
              class="ml-1 interval-badge"
              variant="light-primary"
            >
              Rescan sonrası
            </b-badge>
          </div>
        </div>

        <div class="toolbar-field mode-field">
          <label>Seçim modu</label>
          <b-form-select
            :value="settings.selectionMode || 'SINGLE_LEADER'"
            class="performance-select"
            :options="selectionModeOptions"
            :disabled="saving || !settings.enabled"
            @change="onSelectionModeChange"
          />
        </div>

        <div
          v-if="(settings.selectionMode || 'SINGLE_LEADER') === 'TOP_POOL'"
          class="toolbar-field limit-field"
        >
          <label>Aday sayısı</label>
          <b-form-select
            :value="settings.candidateLimit || 3"
            class="performance-select"
            :options="candidateLimitOptions"
            :disabled="saving || !settings.enabled"
            @change="onCandidateLimitChange"
          />
        </div>

        <div class="toolbar-field grace-field">
          <label>Geçiş koruması</label>
          <b-form-select
            :value="handoffGraceValue"
            class="performance-select"
            :options="handoffGraceOptions"
            :disabled="saving || !settings.enabled"
            @change="onHandoffGraceChange"
          />
        </div>

        <div class="toolbar-actions">
          <b-button
            variant="outline-primary"
            class="mr-1 action-button"
            :disabled="loading"
            @click="fetchData"
          >
            <b-spinner
              v-if="loading"
              small
            />
            <feather-icon
              v-else
              icon="RefreshCwIcon"
            />
            Yenile
          </b-button>
          <b-button
            variant="warning"
            class="action-button"
            :disabled="saving || !settings.enabled"
            @click="runNow"
          >
            <b-spinner
              v-if="saving"
              small
            />
            <feather-icon
              v-else
              icon="ZapIcon"
            />
            Şimdi Kontrol Et
          </b-button>
        </div>
      </b-card-body>
    </b-card>

    <b-alert
      v-if="dataSave.message"
      :variant="dataSave.status ? 'success' : 'danger'"
      show
      dismissible
      class="mb-2"
      @dismissed="$store.commit('indicatorPerformance/SET_DATA_SAVE', { status: null, message: null })"
    >
      {{ dataSave.message }}
    </b-alert>

    <b-card
      no-body
      class="mb-2 performance-summary-card"
    >
      <b-card-body class="performance-summary">
        <div class="summary-header">
          <div>
            <h4 class="mb-0">
              Signal Outcome
            </h4>
            <div class="font-small-3 text-muted">
              Son {{ summaryWindow }} saat · {{ summary.filters.market || market }} · {{ summaryOverview.signals || 0 }} sinyal
            </div>
          </div>
          <b-badge
            variant="light-info"
            class="summary-badge"
          >
            {{ summaryOverview.openedRate || 0 }}% açıldı
          </b-badge>
        </div>

        <div
          class="summary-eligibility"
        >
          <div class="summary-eligibility-header">
            <div>
              <strong>Entry Eligibility</strong>
              <div class="font-small-3 text-muted">
                Açılmayan sinyaller için blok ve eligibility özeti
              </div>
            </div>
            <b-badge
              :variant="summaryOverview.opened > 0 ? 'light-success' : 'light-warning'"
              class="summary-eligibility-badge"
            >
              {{ eligibilityHeadline }}
            </b-badge>
          </div>
          <div class="summary-eligibility-grid">
            <div class="summary-eligibility-metric">
              <span>Aday</span>
              <strong>{{ formatNum(summaryOverview.signals || 0, 0) }}</strong>
            </div>
            <div class="summary-eligibility-metric">
              <span>Eligible</span>
              <strong>{{ formatNum(summaryOverview.opened || 0, 0) }}</strong>
              <small>{{ formatNum(summaryOverview.openedRate || 0, 1) }}%</small>
            </div>
            <div class="summary-eligibility-metric">
              <span>Blocked</span>
              <strong>{{ formatNum(summaryOverview.blocked || 0, 0) }}</strong>
              <small>{{ formatNum(summaryOverview.blockedRate || 0, 1) }}%</small>
            </div>
            <div class="summary-eligibility-metric">
              <span>Opened</span>
              <strong>{{ formatNum(summaryOverview.opened || 0, 0) }}</strong>
              <small>{{ formatNum(summaryOverview.openedRate || 0, 1) }}%</small>
            </div>
          </div>
          <div
            v-if="topBlockedReasonText"
            class="summary-eligibility-note"
          >
            <strong>En baskın blok:</strong>
            <span>{{ topBlockedReasonText }}</span>
          </div>
        </div>

        <div class="summary-grid">
          <div class="summary-metric">
            <span>Opened</span>
            <strong>{{ formatNum(summaryOverview.opened || 0, 0) }}</strong>
            <small>{{ formatNum(summaryOverview.openedRate || 0, 1) }}%</small>
          </div>
          <div class="summary-metric">
            <span>Blocked</span>
            <strong>{{ formatNum(summaryOverview.blocked || 0, 0) }}</strong>
            <small>{{ formatNum(summaryOverview.blockedRate || 0, 1) }}%</small>
          </div>
          <div class="summary-metric">
            <span>Not selected</span>
            <strong>{{ formatNum(summaryOverview.notSelected || 0, 0) }}</strong>
            <small>{{ formatNum(summaryOverview.notSelectedRate || 0, 1) }}%</small>
          </div>
          <div class="summary-metric">
            <span>Good skipped</span>
            <strong>{{ formatNum(summaryOverview.goodSkipped || 0, 0) }}</strong>
            <small>{{ formatNum(summaryOverview.goodSkippedRate || 0, 1) }}%</small>
          </div>
          <div class="summary-metric">
            <span>Bad opened</span>
            <strong>{{ formatNum(summaryOverview.badOpened || 0, 0) }}</strong>
            <small>{{ formatNum(summaryOverview.badOpenedRate || 0, 1) }}%</small>
          </div>
          <div class="summary-metric">
            <span>TP before SL</span>
            <strong>{{ formatNum(summaryOverview.tpBeforeSl || 0, 0) }}</strong>
            <small>{{ formatNum(summaryOverview.tpBeforeSlRate || 0, 1) }}%</small>
          </div>
        </div>

        <div
          v-if="summaryGuidanceText"
          class="summary-guidance"
        >
          <b-badge
            :variant="Number(summaryGuidance.weightFactor || 1) >= 1 ? 'light-success' : 'light-warning'"
            class="summary-guidance-badge"
          >
            {{ Number(summaryGuidance.weightFactor || 1) >= 1 ? 'Destekleyici' : 'Sıkılaştır' }}
          </b-badge>
          <div class="summary-guidance-text">
            <strong>{{ summaryGuidanceBucket }}</strong>
            <span>{{ summaryGuidanceText }}</span>
          </div>
          <div class="summary-guidance-stats">
            <span>W {{ formatNum(summaryGuidance.weightFactor || 1, 2) }}</span>
            <span>ΔS {{ signedNum(summaryGuidance.scoreDelta || 0, 2) }}</span>
            <span>ΔScore {{ signedNum(summaryGuidance.minEntryScoreDelta || 0, 2) }}</span>
            <span>ΔRR {{ signedNum(summaryGuidance.minRemainingRewardRiskDelta || 0, 2) }}</span>
          </div>
        </div>

        <div class="summary-breakdown-grid">
          <div class="summary-breakdown-panel">
            <div class="summary-panel-title">
              Decision Modules
            </div>
            <div
              v-for="item in decisionModuleRows"
              :key="item.bucket"
              class="summary-line"
            >
              <div class="summary-line-head">
                <span class="summary-bucket">{{ item.bucket }}</span>
                <strong>{{ formatNum(item.signals || 0, 0) }}</strong>
              </div>
              <div class="font-small-2 text-muted">
                Opened {{ formatNum(item.openedRate || 0, 1) }}% · Blocked {{ formatNum(item.blockedRate || 0, 1) }}% · Good skip {{ formatNum(item.goodSkippedRate || 0, 1) }}%
              </div>
            </div>
          </div>
          <div class="summary-breakdown-panel">
            <div class="summary-panel-title">
              Families
            </div>
            <div
              v-for="item in familyRows"
              :key="item.bucket"
              class="summary-line"
            >
              <div class="summary-line-head">
                <span class="summary-bucket">{{ item.bucket }}</span>
                <strong>{{ formatNum(item.signals || 0, 0) }}</strong>
              </div>
              <div class="font-small-2 text-muted">
                Opened {{ formatNum(item.openedRate || 0, 1) }}% · Bad opened {{ formatNum(item.badOpenedRate || 0, 1) }}% · TP before SL {{ formatNum(item.tpBeforeSlRate || 0, 1) }}%
              </div>
              <div
                v-if="item.guidance"
                class="font-small-2 summary-guidance-inline"
              >
                <span class="text-muted">
                  Guidance
                </span>
                <strong>{{ formatNum(item.guidance.weightFactor || 1, 2) }}</strong>
                <span class="text-muted">
                  {{ item.guidance.note || 'neutral' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="recentRows.length"
          class="summary-recent"
        >
          <div class="summary-panel-title">
            Recent
          </div>
          <div
            v-for="item in recentRows"
            :key="item.id"
            class="summary-recent-line"
          >
            <span class="summary-badge-mini">{{ item.decisionStatus || 'DETECTED' }}</span>
            <div class="summary-recent-stack">
              <span class="summary-recent-text">
                {{ item.symbol }} {{ item.side }} · {{ recentDecisionLabel(item) }}
              </span>
              <span class="summary-recent-subtext">
                {{ recentDecisionContext(item) }}
              </span>
              <span
                v-if="recentOutcomeDetail(item)"
                class="summary-recent-subtext"
              >
                {{ recentOutcomeDetail(item) }}
              </span>
              <span class="summary-recent-subtext">
                {{ formatDateTime(item.detectedAt) }}
              </span>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <b-card
      no-body
      class="performance-table-card"
    >
      <b-card-body class="table-title-row">
        <div>
          <h4 class="mb-0">
            İndikatör Performansı
          </h4>
          <div class="font-small-3 text-muted">
            Son kontrol: {{ formatDateTime(settings.lastCheckedAt) }} · Son seçim: {{ selectedLabel }} · {{ selectionModeLabel }}
            <span v-if="candidatePoolLabel"> · {{ candidatePoolLabel }}</span>
            <span v-if="settings.handoffUntilAt"> · koruma {{ formatDateTime(settings.handoffUntilAt) }}</span>
          </div>
        </div>
        <b-badge
          class="ml-auto"
          variant="light-info"
        >
          {{ tableRows.length }} indikatör
        </b-badge>
      </b-card-body>

      <b-table
        responsive="sm"
        class="performance-table mb-0"
        :fields="fields"
        :items="tableRows"
        :busy="loading"
        sort-by="overallScore"
        :sort-desc="true"
        striped
        show-empty
        empty-text="Performans verisi bulunamadı."
      >
        <template #cell(rank)="data">
          <b-badge :variant="data.index === 0 ? 'success' : 'light-secondary'">
            #{{ data.index + 1 }}
          </b-badge>
        </template>

        <template #cell(indicator)="data">
          <div class="font-weight-bold">
            {{ data.item.name }}
            <b-badge
              v-if="data.item.isUserActive"
              variant="success"
              class="ml-50"
            >
              Aktif
            </b-badge>
            <b-badge
              v-if="!data.item.isSelectable"
              :variant="healthVariant(data.item.healthStatus)"
              class="ml-50"
            >
              {{ healthLabel(data.item.healthStatus) }}
            </b-badge>
          </div>
          <div class="font-small-2 text-muted">
            {{ data.item.engineKey }} · {{ data.item.family }} v{{ data.item.version }}
          </div>
          <div
            v-if="!data.item.isSelectable"
            class="font-small-2 text-muted"
          >
            {{ data.item.healthReason }}
          </div>
        </template>

        <template #cell(overallScore)="data">
          <strong :class="scoreClass(data.item.overallScore)">{{ formatNum(data.item.overallScore, 3) }}</strong>
          <div class="font-small-2 text-muted">
            {{ data.item.totalEdges }} edge
          </div>
          <b-badge
            v-if="Number(data.item.signalOutcomeContribution || 0) !== 0 || Number(data.item.runtimeContribution || 0) !== 0"
            variant="light-info"
            class="mt-25"
          >
            Canlı bağlam dahil
          </b-badge>
        </template>

        <template #cell(scoreBreakdown)="data">
          <div class="score-breakdown">
            <div>
              Quick {{ signedNum(data.item.quickGainContribution, 3) }}
              · Edge {{ signedNum(data.item.normalContribution, 3) }}
            </div>
            <div>
              Kayıt {{ signedNum(data.item.savedQuickContribution, 3) }}
              · WR {{ signedNum(data.item.winRateContribution, 3) }}
            </div>
            <div>
              MFE {{ signedNum(data.item.mfeContribution, 3) }}
              · Outcome {{ signedNum(data.item.signalOutcomeContribution, 3) }}
            </div>
            <div>
              Risk {{ signedNum(data.item.runtimeContribution, 3) }}
              · Baz {{ formatNum(data.item.baseScore, 3) }}
            </div>
            <div class="text-muted">
              Outcome + runtime katmanı sticky / regime okumasını skor içine taşır.
            </div>
            <div class="font-weight-bold">
              Final {{ formatNum(data.item.overallScore, 3) }}
            </div>
          </div>
        </template>

        <template #cell(normalScore)="data">
          <div><strong :class="scoreClass(data.item.normalScore)">{{ formatNum(data.item.normalScore, 3) }}</strong></div>
          <div class="font-small-2 text-muted">
            {{ data.item.normalCount }} edge · {{ data.item.normalActive }} aktif
          </div>
        </template>

        <template #cell(quickScore)="data">
          <div><strong :class="scoreClass(data.item.quickScore)">{{ formatNum(data.item.quickScore, 3) }}</strong></div>
          <div class="font-small-2 text-muted">
            {{ data.item.quickCount }} edge · {{ data.item.quickActive }} aktif
          </div>
        </template>

        <template #cell(savedScore)="data">
          <b-badge :variant="data.item.savedConfigured ? 'warning' : 'light-secondary'">
            {{ data.item.savedConfigured ? 'Kayıtlı' : 'Yok' }}
          </b-badge>
          <div class="font-small-2 text-muted">
            {{ formatNum(data.item.savedScore, 3) }}
          </div>
        </template>

        <template #cell(winRate)="data">
          <div>Edge {{ formatNum(data.item.normalWinRate, 1) }}%</div>
          <div class="font-small-2 text-muted">
            Quick {{ formatNum(data.item.quickWinRate, 1) }}%
          </div>
        </template>

        <template #cell(mfeQuality)="data">
          <div>
            <strong :class="scoreClass(data.item.mfeQualityScore)">
              +0,50 {{ formatNum(data.item.mfe50Rate, 0) }}%
            </strong>
          </div>
          <div class="font-small-2 text-muted">
            +0,75 {{ formatNum(data.item.mfe75Rate, 0) }}% · {{ data.item.mfeSamples }} işlem
          </div>
          <div class="font-small-2 text-muted">
            MFE {{ formatNum(data.item.avgMfe, 2) }} / MAE {{ formatNum(data.item.avgMae, 2) }}
          </div>
        </template>

        <template #cell(runtimeQuality)="data">
          <div>
            <strong :class="scoreClass(data.item.systemFitScore)">
              Edge sinyali: {{ data.item.entries24h }} / 24s
            </strong>
          </div>
          <div class="font-small-2 text-muted">
            Frekans {{ formatNum(data.item.frequencyScore, 2) }} · Uyum {{ formatNum(data.item.systemFitScore, 2) }}
          </div>
          <div
            class="font-small-2"
            :class="data.item.guardianExitRate >= 35 || data.item.sameSideClusterRate >= 10 ? 'text-warning' : 'text-muted'"
          >
            G {{ formatNum(data.item.guardianExitRate, 0) }}% · Küme {{ formatNum(data.item.sameSideClusterRate, 0) }}%
          </div>
          <div class="font-small-2 text-muted">
            Son edge sinyali: {{ lastSignalLabel(data.item) }}
          </div>
        </template>

        <template #cell(signalOutcome)="data">
          <div class="signal-outcome-cell">
            <div class="signal-outcome-head">
              <strong>{{ (data.item.signalOutcome && data.item.signalOutcome.bucket) || data.item.family || '-' }}</strong>
              <b-badge
                :variant="Number(((data.item.signalOutcome && data.item.signalOutcome.guidance) && data.item.signalOutcome.guidance.weightFactor) || 1) >= 1 ? 'light-success' : 'light-warning'"
              >
                W {{ formatNum((((data.item.signalOutcome && data.item.signalOutcome.guidance) && data.item.signalOutcome.guidance.weightFactor) || 1), 2) }}
              </b-badge>
            </div>
            <div class="font-small-2 text-muted">
              {{ formatNum((data.item.signalOutcome && data.item.signalOutcome.signals) || 0, 0) }} sinyal · Açıldı {{ formatNum((data.item.signalOutcome && data.item.signalOutcome.openedRate) || 0, 1) }}% · Blok {{ formatNum((data.item.signalOutcome && data.item.signalOutcome.blockedRate) || 0, 1) }}%
            </div>
            <div class="font-small-2 text-muted">
              {{ signalOutcomeLabel(data.item.signalOutcome) }}
            </div>
            <b-badge
              v-if="stickySelectionSummary(data.item)"
              variant="light-info"
              class="mt-25"
            >
              {{ stickySelectionSummary(data.item) }}
            </b-badge>
            <div class="font-small-2 text-muted">
              {{ regimeSummaryLabel(data.item.regimeSummary) }}
            </div>
          </div>
        </template>

        <template #cell(netEv)="data">
          <div :class="scoreClass(data.item.normalNetEv)">
            E {{ formatNum(data.item.normalNetEv, 3) }}
          </div>
          <div
            class="font-small-2"
            :class="scoreClass(data.item.quickNetEv)"
          >
            Q {{ formatNum(data.item.quickNetEv, 3) }}
          </div>
        </template>

        <template #cell(trades)="data">
          <div>{{ data.item.totalExits }}</div>
          <div class="font-small-2 text-muted">
            DD {{ formatNum(data.item.maxDrawdown, 2) }}%
          </div>
        </template>

        <template #cell(lastRescannedAt)="data">
          <div class="font-small-3">
            {{ formatDateTime(data.item.lastRescannedAt) }}
          </div>
        </template>

        <template #table-busy>
          <div class="text-center py-2">
            <b-spinner small /> Yükleniyor...
          </div>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BFormCheckbox,
  BFormSelect,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import {
  indicatorRecentDecisionContext,
  indicatorRecentDecisionLabel,
  outcomeMetricsDetail,
  stickySelectionSummary as buildStickySelectionSummary,
} from '@/utils/signalOutcomePresentation'

export default {
  name: 'IndicatorPerformanceIndex',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BFormCheckbox,
    BFormSelect,
    BSpinner,
    BTable,
  },
  data() {
    return {
      market: 'FUTURES',
      marketOptions: [
        { value: 'FUTURES', text: 'Futures' },
        { value: 'SPOT', text: 'Spot' },
      ],
      selectionModeOptions: [
        { value: 'SINGLE_LEADER', text: 'Tek Lider' },
        { value: 'TOP_POOL', text: 'Top Havuz' },
      ],
      candidateLimitOptions: [
        { value: 2, text: 'Top 2' },
        { value: 3, text: 'Top 3' },
        { value: 4, text: 'Top 4' },
        { value: 5, text: 'Top 5' },
      ],
      handoffGraceOptions: [
        { value: 0, text: 'Kapalı' },
        { value: 15, text: '15 dk' },
        { value: 30, text: '30 dk' },
        { value: 60, text: '60 dk' },
        { value: 120, text: '120 dk' },
      ],
      fields: [
        {
          key: 'rank', label: '#', sortable: false, thClass: 'width-60',
        },
        {
          key: 'indicator', label: 'İndikatör', sortable: true, sortByFormatted: false,
        },
        {
          key: 'overallScore', label: 'Genel Skor', sortable: true, thClass: 'width-120',
        },
        {
          key: 'scoreBreakdown', label: 'Skor Detayı', sortable: false, thClass: 'width-180',
        },
        {
          key: 'normalScore', label: 'Edge Scan', sortable: true, thClass: 'width-130',
        },
        {
          key: 'quickScore', label: 'Quick Scan', sortable: true, thClass: 'width-130',
        },
        {
          key: 'savedScore', label: 'Kayıtlı Quick', sortable: true, thClass: 'width-130',
        },
        {
          key: 'winRate', label: 'WR', sortable: true, thClass: 'width-110',
        },
        {
          key: 'mfeQuality', label: 'MFE Kalite', sortable: true, thClass: 'width-150',
        },
        {
          key: 'runtimeQuality', label: 'Canlılık', sortable: true, thClass: 'width-190',
        },
        {
          key: 'signalOutcome', label: 'Outcome', sortable: false, thClass: 'width-190',
        },
        {
          key: 'netEv', label: 'EV', sortable: true, thClass: 'width-100',
        },
        {
          key: 'trades', label: 'Trade', sortable: true, thClass: 'width-90',
        },
        {
          key: 'lastRescannedAt', label: 'Son Scan', sortable: true, thClass: 'width-150',
        },
      ],
    }
  },
  computed: {
    rows() { return this.$store.getters['indicatorPerformance/rows'] },
    summary() { return this.$store.getters['indicatorPerformance/summary'] || {} },
    settings() { return this.$store.getters['indicatorPerformance/settings'] || {} },
    loading() { return this.$store.getters['indicatorPerformance/loading'] },
    saving() { return this.$store.getters['indicatorPerformance/saving'] },
    dataSave() { return this.$store.getters['indicatorPerformance/dataSaveStatus'] },
    summaryOverview() { return this.summary.overview || {} },
    summaryGuidance() { return this.summary.guidance || {} },
    summaryGuidanceBucket() {
      return this.summary.filters?.family
        ? `${this.summary.filters.family} guidance`
        : 'Genel guidance'
    },
    summaryGuidanceText() {
      const guidance = this.summaryGuidance || {}
      const note = guidance.note ? String(guidance.note).replace(/_/g, ' ') : ''
      return guidance.note
        ? `${note} · ${this.formatNum(guidance.samples || 0, 0)} sinyal`
        : ''
    },
    eligibilityHeadline() {
      const signals = Number(this.summaryOverview.signals || 0)
      const opened = Number(this.summaryOverview.opened || 0)
      const blocked = Number(this.summaryOverview.blocked || 0)
      if (!signals) return 'Veri bekleniyor'
      if (opened === 0 && blocked === 0) return 'Eligibility sinyali yok'
      if (opened === 0) return '0 açıldı'
      return `${opened}/${signals} açıldı`
    },
    topBlockedReasonText() {
      const blocked = (this.recentRows || []).find(item => {
        const status = String(item.decisionStatus || '').toUpperCase()
        return status.includes('BLOCK')
      })
      if (!blocked) return ''
      const context = this.recentDecisionContext(blocked)
      const outcome = this.recentOutcomeDetail(blocked)
      return [blocked.symbol, blocked.side, context, outcome].filter(Boolean).join(' · ')
    },
    decisionModuleRows() { return (this.summary.byDecisionModule || []).slice(0, 5) },
    familyRows() { return (this.summary.byFamily || []).slice(0, 5) },
    recentRows() { return (this.summary.recent || []).slice(0, 5) },
    summaryWindow() { return Number(this.summary.filters?.hours || 24) },
    tableRows() {
      return (this.rows || []).map(row => {
        const normal = row.normal || {}
        const quick = row.quickGain || {}
        const saved = row.savedQuickScan || {}
        const mfe = row.mfe || {}
        const runtime = row.runtimeQuality || {}
        const breakdown = row.scoreBreakdown || {}
        return {
          ...row,
          indicator: `${row.name} ${row.engineKey}`,
          quickGainContribution: Number(breakdown.quickGainContribution || 0),
          normalContribution: Number(breakdown.normalContribution || 0),
          savedQuickContribution: Number(breakdown.savedQuickContribution || 0),
          winRateContribution: Number(breakdown.winRateContribution || 0),
          baseScore: Number(breakdown.baseScore || 0),
          mfeContribution: Number(breakdown.mfeContribution || 0),
          signalOutcomeContribution: Number(breakdown.signalOutcomeContribution || 0),
          runtimeContribution: Number(breakdown.runtimeContribution || 0),
          regimeSummary: row.regimeSummary || null,
          normalScore: Number(normal.avgScore || 0),
          normalCount: Number(normal.count || 0),
          normalActive: Number(normal.activeCount || 0),
          normalWinRate: Number(normal.avgWinRate || 0),
          normalNetEv: Number(normal.avgNetEv || 0),
          quickScore: Number(quick.avgScore || 0),
          quickCount: Number(quick.count || 0),
          quickActive: Number(quick.activeCount || 0),
          quickWinRate: Number(quick.avgWinRate || 0),
          quickNetEv: Number(quick.avgNetEv || 0),
          savedConfigured: !!saved.configured,
          savedScore: Number(saved.score || 0),
          mfeSamples: Number(mfe.sampleCount || 0),
          avgMfe: Number(mfe.avgMfe || 0),
          avgMae: Number(mfe.avgMae || 0),
          mfe30Rate: Number(mfe.mfe30Rate || 0),
          mfe50Rate: Number(mfe.mfe50Rate || 0),
          mfe75Rate: Number(mfe.mfe75Rate || 0),
          mfe100Rate: Number(mfe.mfe100Rate || 0),
          mfeQualityScore: Number(mfe.qualityScore || 0),
          entries24h: Number(runtime.entries24h || 0),
          exits24h: Number(runtime.exits24h || 0),
          guardianExits24h: Number(runtime.guardianExits24h || 0),
          guardianExitRate: Number(runtime.guardianExitRate || 0),
          duplicateSameSide24h: Number(runtime.duplicateSameSide24h || 0),
          sameSideClusterRate: Number(runtime.sameSideClusterRate || 0),
          frequencyScore: Number(runtime.frequencyScore || 0),
          systemFitScore: Number(runtime.systemFitScore || 0),
          lastSignalAt: runtime.lastSignalAt || null,
          lastSignalSymbol: runtime.lastSignalSymbol || null,
          lastSignalSide: runtime.lastSignalSide || null,
          lastSignalPrice: runtime.lastSignalPrice === null || runtime.lastSignalPrice === undefined ? null : Number(runtime.lastSignalPrice),
          lastSignalScore: runtime.lastSignalScore === null || runtime.lastSignalScore === undefined ? null : Number(runtime.lastSignalScore),
          totalExits: Number(normal.totalExits || 0) + Number(quick.totalExits || 0),
          winRate: Math.max(Number(normal.avgWinRate || 0), Number(quick.avgWinRate || 0)),
          mfeQuality: Number(mfe.qualityScore || 0),
          runtimeQuality: Number(runtime.systemFitScore || 0),
          netEv: Math.max(Number(normal.avgNetEv || 0), Number(quick.avgNetEv || 0)),
          trades: Number(normal.totalExits || 0) + Number(quick.totalExits || 0),
          maxDrawdown: Math.max(Number(normal.maxDrawdown || 0), Number(quick.maxDrawdown || 0)),
          activeEdges: Number(row.activeEdges || 0),
          isSelectable: row.isSelectable !== false,
          healthStatus: row.healthStatus || 'OK',
          healthReason: row.healthReason || '',
          lastRescannedAt: row.lastRescannedAt || quick.lastRescannedAt || normal.lastRescannedAt,
        }
      })
    },
    selectedLabel() {
      if (!this.settings.lastSelectedIndicatorId) return '—'
      const row = this.rows.find(item => item.indicatorId === this.settings.lastSelectedIndicatorId)
      return row ? row.name : `#${this.settings.lastSelectedIndicatorId}`
    },
    selectionModeLabel() {
      return (this.settings.selectionMode || 'SINGLE_LEADER') === 'TOP_POOL'
        ? `Top ${Number(this.settings.candidateLimit || 3)} havuz`
        : 'Tek lider'
    },
    candidatePoolLabel() {
      const ids = this.settings.candidateIndicatorIds || []
      if (!ids.length) return ''
      return `aday ${ids.length}`
    },
    handoffGraceValue() {
      return this.settings.handoffGraceMinutes === null || this.settings.handoffGraceMinutes === undefined
        ? 30
        : Number(this.settings.handoffGraceMinutes)
    },
  },
  watch: {
    settings: {
      immediate: true,
      handler(val) {
        if (val?.market && val.market !== this.market) this.market = val.market
      },
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    fetchData() {
      return this.$store.dispatch('indicatorPerformance/fetch', { market: this.market })
    },
    onMarketChange() {
      this.updateAutoSelectionSettings({ market: this.market })
    },
    onToggleAuto(value) {
      this.updateAutoSelectionSettings({ enabled: !!value })
    },
    onSelectionModeChange(value) {
      this.updateAutoSelectionSettings({
        selectionMode: value,
        candidateLimit: value === 'TOP_POOL' ? Number(this.settings.candidateLimit || 3) : 1,
      })
    },
    onCandidateLimitChange(value) {
      this.updateAutoSelectionSettings({ candidateLimit: Number(value || 3) })
    },
    onHandoffGraceChange(value) {
      this.updateAutoSelectionSettings({ handoffGraceMinutes: Number(value ?? 30) })
    },
    updateAutoSelectionSettings(patch) {
      this.$store.dispatch('indicatorPerformance/updateSettings', {
        enabled: patch.enabled !== undefined ? !!patch.enabled : !!this.settings.enabled,
        market: patch.market || this.market,
        selectionMode: patch.selectionMode || this.settings.selectionMode || 'SINGLE_LEADER',
        candidateLimit: patch.candidateLimit !== undefined
          ? Number(patch.candidateLimit)
          : Number(this.settings.candidateLimit || ((this.settings.selectionMode || 'SINGLE_LEADER') === 'TOP_POOL' ? 3 : 1)),
        handoffGraceMinutes: patch.handoffGraceMinutes !== undefined
          ? Number(patch.handoffGraceMinutes)
          : Number(this.settings.handoffGraceMinutes ?? 30),
      })
    },
    runNow() {
      this.$store.dispatch('indicatorPerformance/runNow')
    },
    formatNum(value, digits = 2) {
      const n = Number(value || 0)
      if (!Number.isFinite(n)) return '0'
      return n.toLocaleString('tr-TR', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    },
    signedNum(value, digits = 2) {
      const n = Number(value || 0)
      if (!Number.isFinite(n)) return '0'
      const formatted = this.formatNum(Math.abs(n), digits)
      if (n > 0) return `+${formatted}`
      if (n < 0) return `-${formatted}`
      return formatted
    },
    lastSignalLabel(item) {
      if (!item.lastSignalAt) return '—'
      const side = item.lastSignalSide ? String(item.lastSignalSide) : ''
      const symbol = item.lastSignalSymbol || ''
      const price = item.lastSignalPrice ? ` @ ${this.formatNum(item.lastSignalPrice, 6)}` : ''
      const time = this.formatDateTime(item.lastSignalAt)
      return `${symbol} ${side}${price} · ${time}`.trim()
    },
    signalOutcomeLabel(item) {
      const outcome = item.signalOutcome || item || {}
      const guidance = outcome.guidance || {}
      const note = guidance.note || 'neutral'
      let scope = 'family'
      if (outcome.scope === 'INDICATOR') scope = 'indicator'
      if (outcome.scope === 'BLENDED') scope = 'blend'
      const confidence = this.formatNum(outcome.confidence || 0, 2)
      return `${scope} · conf ${confidence} · ${String(note).replace(/_/g, ' ')}`
    },
    regimeSummaryLabel(summary) {
      const regime = summary || {}
      if (!regime.primaryLabel) return 'Rejim: veri yok'
      return `Rejim ${regime.primaryLabel} · ${this.formatNum(regime.primaryShare || 0, 0)}% · ${this.formatNum(regime.totalSignals || 0, 0)} sinyal`
    },
    recentDecisionLabel(item) {
      return indicatorRecentDecisionLabel(item)
    },
    recentDecisionContext(item) {
      return indicatorRecentDecisionContext(item, this.formatNum)
    },
    recentOutcomeDetail(item) {
      return outcomeMetricsDetail(item, this.formatNum)
    },
    stickySelectionSummary(item) {
      return buildStickySelectionSummary(item, this.formatNum)
    },
    formatDateTime(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '—'
      return date.toLocaleString('tr-TR')
    },
    scoreClass(value) {
      const n = Number(value || 0)
      if (n > 0) return 'text-success'
      if (n < 0) return 'text-danger'
      return 'text-muted'
    },
    healthLabel(status) {
      return {
        OK: 'Canlı',
        NO_EDGES: 'Edge yok',
        STALE: 'Eski',
        DISABLED: 'Pasif',
      }[status] || 'Kontrol'
    },
    healthVariant(status) {
      return {
        OK: 'light-success',
        NO_EDGES: 'light-secondary',
        STALE: 'light-warning',
        DISABLED: 'light-danger',
      }[status] || 'light-secondary'
    },
  },
}
</script>

<style scoped>
.performance-toolbar-card,
.performance-summary-card,
.performance-table-card {
  border: 1px solid #e7e7f2;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(34, 41, 47, 0.04);
  background: #fff;
}

.performance-toolbar {
  display: grid;
  grid-template-columns: minmax(180px, 240px) minmax(260px, 1fr) auto;
  gap: 1rem;
  align-items: end;
  padding: 1rem;
}

.toolbar-field label {
  display: block;
  margin-bottom: 0.35rem;
  color: #6e6b7b;
  font-size: 0.8rem;
  font-weight: 600;
}

.performance-select {
  min-height: 38px;
  border: 1px solid #d8d6de;
  border-radius: 6px;
  background-color: #fff;
  color: #5e5873;
}

.auto-control {
  display: flex;
  align-items: center;
  min-height: 38px;
}

.performance-switch {
  margin-bottom: 0;
  color: #5e5873;
  font-weight: 600;
}

.interval-badge {
  padding: 0.45rem 0.6rem;
  font-size: 0.82rem;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 38px;
  white-space: nowrap;
}

.action-button {
  min-height: 38px;
  border-radius: 6px;
  font-weight: 600;
}

.performance-summary {
  padding: 1rem;
}

.summary-eligibility {
  margin-top: 0.25rem;
  margin-bottom: 1rem;
  padding: 0.9rem 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  background: #fcfcfe;
}

.summary-eligibility-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.summary-eligibility-badge {
  white-space: nowrap;
}

.summary-eligibility-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.summary-eligibility-metric {
  padding: 0.75rem 0.8rem;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  background: #fff;
}

.summary-eligibility-metric span {
  display: block;
  color: #6e6b7b;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

.summary-eligibility-metric strong {
  display: block;
  color: #5e5873;
  font-size: 1.05rem;
}

.summary-eligibility-metric small {
  color: #b9b9c3;
}

.summary-eligibility-note {
  margin-top: 0.75rem;
  font-size: 0.85rem;
  color: #6e6b7b;
}

.summary-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.summary-badge {
  align-self: flex-start;
  min-height: 32px;
  padding: 0.35rem 0.65rem;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.summary-metric {
  padding: 0.85rem 0.9rem;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  background: #fafafe;
}

.summary-metric span,
.summary-metric small {
  display: block;
  color: #6e6b7b;
}

.summary-metric span {
  font-size: 0.78rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
}

.summary-metric strong {
  display: block;
  font-size: 1.15rem;
  line-height: 1.2;
}

.summary-metric small {
  font-size: 0.76rem;
  margin-top: 0.25rem;
}

.summary-breakdown-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.summary-breakdown-panel {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  padding: 0.85rem;
}

.summary-guidance {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.85rem 0.95rem;
  margin-bottom: 1rem;
  border: 1px solid #dfe7ff;
  border-radius: 6px;
  background: #f8fbff;
}

.summary-guidance-badge {
  min-height: 28px;
}

.summary-guidance-text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.summary-guidance-text strong,
.summary-guidance-text span {
  min-width: 0;
}

.summary-guidance-text span {
  color: #6e6b7b;
  font-size: 0.82rem;
}

.summary-guidance-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
  color: #5e5873;
  font-size: 0.76rem;
  font-weight: 600;
}

.summary-guidance-stats span {
  padding: 0.2rem 0.45rem;
  border-radius: 4px;
  background: #eef2ff;
}

.summary-guidance-inline {
  display: flex;
  gap: 0.4rem;
  align-items: center;
  margin-top: 0.2rem;
}

.signal-outcome-cell {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.signal-outcome-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.summary-panel-title {
  margin-bottom: 0.6rem;
  color: #5e5873;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0;
}

.summary-line {
  padding: 0.5rem 0;
  border-top: 1px solid #f1eff7;
}

.summary-line:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.summary-line-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.1rem;
}

.summary-bucket {
  display: inline-block;
  min-width: 0;
  color: #5e5873;
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.summary-recent {
  margin-top: 1rem;
}

.summary-recent-line {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.45rem 0;
  border-top: 1px solid #f1eff7;
}

.summary-recent-line:first-of-type {
  border-top: 0;
}

.summary-badge-mini {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0.18rem 0.45rem;
  border-radius: 4px;
  background: #f1eff7;
  color: #5e5873;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.summary-recent-text {
  color: #6e6b7b;
  font-size: 0.82rem;
}

.summary-recent-stack {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.summary-recent-subtext {
  color: #8a8598;
  font-size: 0.76rem;
}

.table-title-row {
  display: flex;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #ebe9f1;
}

.performance-table {
  color: #5e5873;
}

.performance-table ::v-deep thead th {
  background: #f8f8fb;
  color: #5e5873;
  border-top: 0;
  border-bottom: 1px solid #ebe9f1;
  font-weight: 700;
  white-space: nowrap;
}

.performance-table ::v-deep tbody td {
  vertical-align: middle;
  border-color: #ebe9f1;
}

.score-breakdown {
  color: #6e6b7b;
  font-size: 0.78rem;
  line-height: 1.35;
  white-space: nowrap;
}

::v-deep .table td,
::v-deep .table th {
  vertical-align: middle;
}

@media (max-width: 991.98px) {
  .performance-toolbar {
    grid-template-columns: 1fr;
  }

  .summary-eligibility-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .summary-grid,
  .summary-breakdown-grid {
    grid-template-columns: 1fr;
  }

  .toolbar-actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .summary-eligibility-header {
    flex-direction: column;
  }
}

@media (max-width: 575.98px) {
  .performance-toolbar,
  .performance-summary {
    padding: 0.75rem;
  }

  .summary-eligibility-grid {
    grid-template-columns: 1fr;
  }
}
</style>
